/**
 * Shared API utilities - common functions used across API modules
 */

/**
 * Get the base URL for API requests
 * Checks VITE_API_BASE_URL environment variable, falls back to staging backend
 * @returns {string} Base URL for API requests
 */
export const getBaseUrl = () => {
  let base =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
    'https://ac-backend-stagging.up.railway.app';
  // Prevent mixed-content blocks: when the app itself is served over HTTPS, never
  // call an http:// API (a misconfigured VITE_API_BASE_URL would otherwise be
  // blocked by the browser). Local http dev (e.g. http://localhost) is untouched.
  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    typeof base === 'string' &&
    base.startsWith('http://')
  ) {
    base = 'https://' + base.slice('http://'.length);
  }
  return base;
};

/**
 * Get the Shopify API base URL (may differ from main API base URL)
 * Checks VITE_SHOPIFY_API_BASE_URL environment variable, falls back to aerochat-v1.onrender.com
 * @returns {string} Base URL for Shopify sync APIs
 */
export const getShopifyApiBaseUrl = () =>
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SHOPIFY_API_BASE_URL) ||
  'https://dev-shopify-production.up.railway.app';

/**
 * Get the embedded Shopify app's handle from the Partner Dashboard
 * (e.g. "ac-bfs-staging"). Mirrors the backend's SHOPIFY_EMBEDDED_APP_HANDLE,
 * which builds the same admin deep links server-side.
 *
 * Deliberately has no fallback: the handle differs per environment, and a
 * wrong one silently sends merchants to another app's URL. Callers must treat
 * '' as "not configured" rather than building a link from it.
 *
 * @returns {string} App handle, or '' when unconfigured
 */
export const getShopifyEmbeddedAppHandle = () =>
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SHOPIFY_EMBEDDED_APP_HANDLE) ||
  '';

/**
 * Get the widget app URL (the customer-facing chat widget)
 * Checks VITE_WIDGET_APP_URL environment variable, falls back to ac-react.onrender.com
 * @returns {string} Base URL for the widget app
 */
export const getWidgetAppUrl = () =>
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WIDGET_APP_URL) ||
  'https://ac-react.onrender.com';

/**
 * Parse API response and handle errors
 * Handles various response formats:
 * - { success: true/false, data: {...}, message: "..." }
 * - { status: "success", data: {...} }
 * - Direct data objects
 * 
 * @param {Response} res - Fetch response object
 * @returns {Promise<object>} Parsed data from response
 * @throws {Error} If request failed or success is false
 */
export async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  
  // Check for success: false first (even if res.ok is true, backend may return success: false)
  if (data.success === false) {
    const message = data.message || data.error || 'Request failed';
    throw new Error(message);
  }
  
  // Handle HTTP error status codes
  if (!res.ok) {
    const message = data?.message || data?.error || res.statusText || 'Request failed';
    throw new Error(message);
  }
  
  // Extract data from response
  if (data.success === true && data.data) {
    return data.data;
  }
  if (data.status === 'success' && data.data) {
    return data.data;
  }
  return data.data != null ? data.data : data;
}

/**
 * Get the WordPress plugin download URL
 * Checks VITE_WP_PLUGIN_URL environment variable
 * @returns {string} Download URL for the AeroChat WordPress plugin
 */
export const getWpPluginUrl = () =>
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WP_PLUGIN_URL) || '';

/**
 * Build a clean URL by removing duplicate slashes
 * @param {string} base - Base URL
 * @param {string} path - Path to append
 * @returns {string} Clean URL
 */
export function buildUrl(base, path) {
  return `${base}/${path}`.replace(/([^:]\/)\/+/g, '$1');
}
